import type { PackageFormTripType } from '@/components/Packages/types'

export type GA4ProductType = 'air' | 'hotel' | 'car' | 'bundle' | 'cruise' | 'penny'
export type GA4PageNameType = 'homepage' | 'landing' | 'sign_in' | 'account_creation'

export const GA4ProductMap = {
  1: 'air',
  5: 'hotel',
  8: 'car',
} as const

export type GA4PackageFormAdvancedSearchLink = {
  event: 'select_promotion' | 'view_promotion'
  attributes: {
    type: PackageFormTripType
    page_name: GA4PageNameType
    product_name: 'bundle'
    link_name: 'advanced_search'
  }
}

export type GA4OnSearchButtonClickForSuccessfulSearchTrigger = {
  event: 'internal_link'
  attributes: {
    link_item: PackageFormTripType
    page_name: GA4PageNameType
    product_name: 'bundle'
    link_name: 'search_button'
  }
}

export type GA4PackageCarousel = {
  event: 'select_promotion' | 'view_promotion'
  attributes: {
    type: 'package'
    page_name: GA4PageNameType
    product_name: GA4ProductType
    promotion_id: 'deal_tile'
    promotion_name: 'hotel_air'
    location_id: 'carousel'
    ecommerce: Readonly<{
      items?: ReadonlyArray<{
        item_id: string
        item_name: string
        price: number
        promotion_id: string
        promotion_name: string
      }>
      transaction_id?: string
    }>
  }
}

export type GA4PackagePromoEvent = {
  event: 'view_promotion' | 'select_promotion'
  attributes: {
    type: 'marketing'
    bundle_flag: 'y'
    bundle_type: string
    product_name: GA4ProductType
    promotion_name: string
    creative_name: string
    items: Array<{
      index: string
      item_name: string
      item_id: string
    }>
  }
}

export type GA4SignIn = {
  event: 'login'
  attributes: {
    method: string
    product_name?: string
    page_name?: string
    success_flag?: 'y' | 'n'
  }
}

export type GA4SocialSignIn = {
  event: 'internal_link'
  attributes: {
    type: 'sign_in'
    link_name: 'sign_in_with_social'
    link_item: 'google' | 'facebook' | 'apple' | (string & {})
    page_name: 'sign_in'
  }
}

export type GA4PennyTypeahead = {
  event: 'internal_link'
  attributes: {
    link_name: 'penny'
    link_item: 'type_ahead'
    page_name: GA4PageNameType
    product_name: 'hotel'
  }
}

export type GA4PennyIconDisplay = {
  event: 'display'
  attributes: {
    type: 'penny'
    page_name: GA4PageNameType
    product_name: GA4ProductType | null
  }
}

export type GA4PennyTooltipDisplay = {
  event: 'display'
  attributes: {
    type: 'penny'
    item_name: 'tooltips'
    page_name: GA4PageNameType
    product_name: GA4ProductType | null
  }
}

export type GA4PennyTooltipClick = {
  event: 'internal_link'
  attributes: {
    type: 'penny'
    link_name: 'tooltips'
    link_text: 'chat_with_penny'
    page_name: GA4PageNameType
    product_name: GA4ProductType | null
  }
}

export type GA4SearchForm = {
  event: 'search'
  attributes: {
    date_change: 'y' | 'n'
    trip_start_city_change: 'y' | 'n'
    trip_end_city_change: 'y' | 'n'
    quantity_change: 'y' | 'n'
    page_name: GA4PageNameType
    product_name: GA4ProductType
    same_dropoff_location_change?: 'y' | 'n'
    cabin_class_change?: 'y' | 'n'
  }
}
export type GA4MarketingPromotionEvent = {
  event: 'select_promotion' | 'view_promotion'
  attributes: {
    type: string
    page_name: GA4PageNameType
    product_name: GA4ProductType
    promotion_name?: string
    creative_slot?: string
    creative_name?: string
  }
}
export type GA4BundlePromotionEvent = {
  event: 'select_promotion' | 'view_promotion'
  attributes: {
    type: 'add_on'
    product_name: GA4ProductType
    bundle_flag: 'y'
    bundle_type: string
    promotion_name?: string
    page_name?: GA4PageNameType
  }
}

export type GA4SelectContent = {
  event: 'select_content' | 'display'
  attributes: {
    type: 'search_form'
    product_name: GA4ProductType
    item_name: string
    page_name?: GA4PageNameType
  }
}

export type GA4Display = {
  event: 'display'
  attributes: {
    type: 'search_form'
    product_name: GA4ProductType
    item_name: string | null
    page_name?: GA4PageNameType
  }
}

export type GA4SearchRedirectErrorDisplay = {
  event: 'display'
  attributes: {
    type: 'pkg_landing_search_redirect_error'
    item_name: 'search_data_persisted' | ''
    page_name: 'landing_page'
    product_name: 'bundle'
  }
}

export type GA4InternalEvent = {
  event: 'internal_element' | 'internal_link'
  attributes: {
    page_name: GA4PageNameType
    link_name: string
    product_name?: GA4ProductType
    type?: 'search_form' | 'penny' | 'trip_grouping' | 'collapsed_search_form' | 'sign_in' | 'sign_up'
    link_item?: string
    link_text?: string
  }
}

export type GA4PageViewEvent = {
  event: 'page_view'
  attributes: {
    page_name: GA4PageNameType
    page_desc?: GA4ProductType
  }
}
// Express Deals & Cruise Form
export type GA4AbnormalSearchEvent = {
  event: 'search'
  attributes: {
    product_name: GA4ProductType
    page_name: GA4PageNameType
    item_name?: string
  }
}

type GA4TripActivityAddonSelection = {
  event: 'select_promotion'
  attributes: {
    type: 'add_on'
    creative_name: 'pick_up_where_you_left_off'
    creative_slot: 'selection'
    page_name: GA4PageNameType
    product_name: 'air'
    bundle_flag: 'y'
    bundle_type: string
  }
}

type GA4TripActivityRecentActivityPromotion = {
  event: 'select_promotion' | 'view_promotion'
  attributes: {
    type: 'recent_activity'
    creative_name: 'pick_up_where_you_left_off'
    creative_slot: 'search' | 'selection' | 'grouped_selection'
    page_name: GA4PageNameType
    product_name?: GA4ProductType
    items?: ReadonlyArray<{
      item_name: string
      item_category?: 'price_drop' | 'price_increase' | 'price_unchanged'
    }>
  }
}

type GA4TripActivityDisplay = {
  event: 'display'
  attributes: {
    type: 'link'
    item_name: 'view_all_recent_activity'
    product_name: GA4ProductType
    page_name: GA4PageNameType
  }
}

type GA4TripActivityInternalLink = {
  event: 'internal_link'
  attributes: {
    link_name: 'view_all_recent_activity'
    product_name: GA4ProductType
    page_name: GA4PageNameType
  }
}

type GA4AppHandoffBannerDisplay = {
  event: 'display'
  attributes: {
    type: 'your_upcoming_trips'
    item_name: 'app_download'
    page_name: 'homepage'
  }
}

type GA4AppHandoffBannerClick = {
  event: 'internal_link'
  attributes: {
    type: 'your_upcoming_trips'
    item_name: 'app_download'
    page_name: 'homepage'
  }
}

export type GA4TripActivity =
  | GA4TripActivityRecentActivityPromotion
  | GA4TripActivityDisplay
  | GA4TripActivityInternalLink
  | GA4TripActivityAddonSelection
  | GA4AppHandoffBannerDisplay
  | GA4AppHandoffBannerClick

export type GA4HotelSearchFormFilterChip = {
  event: 'filter'
  attributes: {
    filter_type: 'search_form'
    filter_name: string
    filter_item: string
    page_name: 'homepage'
    product_name: 'hotel'
    enabled_flag: 'y' | 'n'
  }
}

export type GA4HotelSearchFormFilter = {
  event: 'display'
  attributes: {
    type: 'search_form'
    item_name: 'filter'
    page_name: 'homepage'
    product_name: 'hotel'
  }
}

export type GA4SearchFormModalExit = {
  event: 'exit_method'
  attributes: {
    method: 'close' | 'swipe_back'
    page_name: GA4PageNameType
    link_name: string
    product_name?: GA4ProductType
    type?: 'collapsed_search_form'
    link_item?: string
  }
}

export type GA4AddToWishlist = {
  event: 'add_to_wishlist'
  attributes: {
    page_name: GA4PageNameType
    product_name: GA4ProductType
    enabled_flag: 'y' | 'n'
    item_list_id?: 'pick_up_where_you_left_off'
  }
}

export type GA4OktaEmailOTPVerification = {
  event: 'api_call'
  attributes: {
    type: 'code_verification'
    page_name: GA4PageNameType
    item_name: 'sign_up' | 'sign_in_verified' | 'sign_in_unverified'
  }
}

export type GA4FindMyTripRedirect = {
  event: 'internal_link'
  attributes: {
    type: 'find_trip'
    link_name: 'otp_verify' | 'initial_signin'
    page_name: 'sign_in'
  }
}

export type GA4AiSearchTab = {
  event: 'internal_link'
  attributes: {
    type: 'ai_mode_penny_search'
    link_name: 'ai_mode_typeahead' | 'ai_mode_search_form_tab'
    page_name: GA4PageNameType
    product_name: 'penny'
  }
}
